﻿import { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { useFormSubmit } from "../../hooks/useFormSubmit";
import Header from "../../components/feature/Header";
import Breadcrumbs from "../../components/feature/Breadcrumbs";
import Footer from "../../components/feature/Footer";
import WhatsAppFloating from "../../components/feature/WhatsAppFloating";
import CookieBanner from "../../components/feature/CookieBanner";
import CrossLinksFooter from "../../components/feature/CrossLinksFooter";
import SEO from "../../components/feature/SEO";
import FormSkeleton from "../../components/FormSkeleton";

const ciudades = [
  "Málaga",
  "Marbella",
  "Fuengirola",
  "Torremolinos",
  "Benalmádena",
  "Mijas",
  "Estepona",
  "Rincón de la Victoria",
  "Vélez-Málaga",
  "Nerja",
  "Antequera",
  "Ronda",
  "Alhaurín de la Torre",
  "Alhaurín el Grande",
  "Coín",
  "Torrox",
  "Frigiliana",
  "Benahavís",
  "San Pedro de Alcántara",
  "Manilva",
  "Casares",
  "Ojén",
  "Istán",
  "Archidona",
  "Almogía",
  "Cártama",
  "Pizarra",
  "Álora",
  "Algarrobo",
  "Cómpeta",
  "Sayalonga",
];

const tipoTrabajo = [
  "Reparación",
  "Instalación nueva",
  "Sustitución",
  "Motorización",
  "No estoy seguro",
];

const comoNosConociste = [
  "Google / Buscador",
  "Recomendación",
  "Redes sociales",
  "Otro",
];

const steps = [
  { icon: "📝", title: "Rellena el formulario o llámanos", desc: "Cuéntanos qué necesitas. Tardas menos de 2 min." },
  { icon: "📞", title: "Te contactamos en menos de 24h", desc: "Un técnico te llama para entender tu caso." },
  { icon: "🏠", title: "Visita técnica GRATUITA + medición", desc: "Si hace falta, pasamos a medir sin coste." },
  { icon: "💰", title: "Presupuesto detallado por escrito", desc: "Precio cerrado. Sin sorpresas ni letra pequeña." },
];

export default function Presupuesto() {
  const [agreed, setAgreed] = useState(false);
  const [service, setService] = useState("");
  const [tipoVivienda, setTipoVivienda] = useState("");
  const [ready, setReady] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmit = useFormSubmit(agreed);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Pídenos tu Presupuesto Gratis | Persianas Toldos Málaga"
        description="Sin compromiso. Te respondemos en 24h con un presupuesto detallado para persianas, toldos o mosquiteras."
        canonical="/presupuesto/"
        ogImage="https://persianas-toldos-malaga.es/og/og-default.jpg"
        ogType="website"
        breadcrumbs={[
          { name: "Inicio", path: "/" },
          { name: "Presupuesto" },
        ]}
      />
      <Header />
      <Breadcrumbs />
      <main>
        {/* HERO */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <motion.span
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-block bg-primary/10 text-primary font-body font-bold text-sm px-4 py-1.5 rounded-full mb-5"
            >
              ⚡ Respuesta en 24h
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-heading font-extrabold text-3xl md:text-5xl text-text-primary mb-5 leading-tight"
            >
              Pídenos tu Presupuesto Gratis
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="font-body text-lg text-text-secondary max-w-2xl mx-auto mb-8"
            >
              Sin compromiso. Te respondemos en 24h con un presupuesto detallado para persianas, toldos o mosquiteras.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <a
                href="tel:+34951120770"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-base px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
              >
                <span>📞</span> 951 12 07 70
              </a>
              <a
                href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-base px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i> WhatsApp
              </a>
            </motion.div>
          </div>
        </section>

        {/* ¿CÓMO FUNCIONA? */}
        <section className="py-16 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-extrabold text-2xl md:text-[32px] text-text-primary text-center mb-12">
              ¿Cómo funciona?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="text-center p-6 border border-border rounded-2xl hover:border-primary/30 transition-colors"
                >
                  <div className="text-4xl mb-3">{step.icon}</div>
                  <h3 className="font-heading font-bold text-base text-text-primary mb-2">{step.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FORMULARIO EXTENDIDO */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl p-6 md:p-10 shadow-sm min-h-[600px]"
            >
              <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-text-primary mb-2">
                Rellena el formulario
              </h2>
              <p className="font-body text-sm text-text-secondary mb-8">
                Campos marcados con <span className="text-primary">*</span> son obligatorios.
              </p>

              {!ready ? (
                <FormSkeleton />
              ) : (
                <form
                  ref={formRef}
                  action="https://formspree.io/f/mdajpjzd"
                  method="POST"
                  encType="application/x-www-form-urlencoded"
                  data-readdy-form
                  id="presupuesto-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                  {/* Nombre y apellidos */}
                  <div>
                    <label htmlFor="nombre" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      Nombre y apellidos <span className="text-primary">*</span>
                    </label>
                    <input
                      id="nombre"
                      name="nombre"
                      type="text"
                      required
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      placeholder="Tu nombre completo"
                    />
                  </div>

                  {/* Teléfono + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="telefono" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                        Teléfono <span className="text-primary">*</span>
                      </label>
                      <div className="flex">
                        <span className="inline-flex items-center px-4 border border-r-0 border-border rounded-l-xl bg-slate-50 font-body text-sm text-text-secondary shrink-0">
                          +34
                        </span>
                        <input
                          id="telefono"
                          name="telefono"
                          type="tel"
                          required
                          className="w-full h-12 px-4 border border-border rounded-r-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                          placeholder="612 34 56 78"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                        Email <span className="text-primary">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  {/* Ciudad */}
                  <div>
                    <label htmlFor="ciudad" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      Ciudad <span className="text-primary">*</span>
                    </label>
                    <select
                      id="ciudad"
                      name="ciudad"
                      required
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none"
                    >
                      <option value="">Selecciona tu ciudad...</option>
                      {ciudades.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                      <option value="Otra zona">Otra zona</option>
                    </select>
                  </div>

                  {/* Servicio — radios */}
                  <div>
                    <span className="block font-body font-medium text-sm text-text-primary mb-2">
                      Servicio <span className="text-primary">*</span>
                    </span>
                    <div className="flex flex-wrap gap-3">
                      {["Persianas", "Toldos", "Mosquiteras", "Combinación"].map((s) => (
                        <label
                          key={s}
                          className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl cursor-pointer transition-all font-body text-sm ${
                            service === s
                              ? "border-primary bg-primary/5 text-primary font-semibold"
                              : "border-border text-text-secondary hover:border-primary/40"
                          }`}
                        >
                          <input
                            type="radio"
                            name="servicio"
                            value={s}
                            required
                            checked={service === s}
                            onChange={() => setService(s)}
                            className="sr-only"
                          />
                          {s}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Vivienda o local — radios */}
                  <div>
                    <span className="block font-body font-medium text-sm text-text-primary mb-2">
                      ¿Es vivienda o local comercial? <span className="text-primary">*</span>
                    </span>
                    <div className="flex flex-wrap gap-3">
                      {["Vivienda", "Local comercial", "Comunidad", "Otro"].map((t) => (
                        <label
                          key={t}
                          className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl cursor-pointer transition-all font-body text-sm ${
                            tipoVivienda === t
                              ? "border-primary bg-primary/5 text-primary font-semibold"
                              : "border-border text-text-secondary hover:border-primary/40"
                          }`}
                        >
                          <input
                            type="radio"
                            name="tipo_inmueble"
                            value={t}
                            required
                            checked={tipoVivienda === t}
                            onChange={() => setTipoVivienda(t)}
                            className="sr-only"
                          />
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Tipo de trabajo */}
                  <div>
                    <label htmlFor="tipo_trabajo" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      Tipo de trabajo <span className="text-primary">*</span>
                    </label>
                    <select
                      id="tipo_trabajo"
                      name="tipo_trabajo"
                      required
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none"
                    >
                      <option value="">Selecciona...</option>
                      {tipoTrabajo.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* Unidades */}
                  <div>
                    <label htmlFor="unidades" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      ¿Cuántas unidades?
                    </label>
                    <input
                      id="unidades"
                      name="unidades"
                      type="number"
                      min="1"
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      placeholder="Ej: 3"
                    />
                  </div>

                  {/* Mensaje */}
                  <div>
                    <label htmlFor="mensaje" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      Mensaje <span className="text-text-secondary">(opcional)</span>
                    </label>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                      placeholder="Cuéntanos detalles: medidas, materiales, urgencia..."
                    ></textarea>
                    <p className="mt-1 text-xs text-text-secondary font-body">Máximo 500 caracteres</p>
                  </div>

                  {/* ¿Cómo nos conociste? */}
                  <div>
                    <label htmlFor="como_nos_conociste" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                      ¿Cómo nos conociste?
                    </label>
                    <select
                      id="como_nos_conociste"
                      name="como_nos_conociste"
                      className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none"
                    >
                      <option value="">Selecciona...</option>
                      {comoNosConociste.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Privacidad */}
                  <div className="flex items-start gap-2.5">
                    <input
                      id="privacidad"
                      name="privacidad"
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary shrink-0"
                    />
                    <label htmlFor="privacidad" className="font-body text-sm text-text-secondary leading-relaxed">
                      He leído y acepto la{" "}
                      <a href="/politica-privacidad" className="underline text-secondary hover:text-secondary-dark transition-colors">
                        Política de Privacidad
                      </a>{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <p className="font-body text-xs text-text-secondary/70 mt-1 leading-relaxed">
                      Responsable: Persianas Toldos Málaga. Finalidad: responder a tu consulta y enviarte presupuesto. Legitimación: tu consentimiento. Destinatarios: no se cederán datos a terceros. Derechos: puedes ejercer tus derechos de acceso, rectificación y supresión escribiendo a info@persianas-toldos-malaga.es
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="w-full h-14 bg-text-primary hover:bg-slate-800 text-white font-heading font-bold text-lg rounded-pill transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    Pedir presupuesto gratis <span>→</span>
                  </button>

                  <p className="text-center font-body text-sm text-text-secondary">
                    o llámanos al{" "}
                    <a href="tel:+34951120770" className="font-semibold text-primary hover:text-primary-dark underline">
                      951 12 07 70
                    </a>{" "}
                    |{" "}
                    <a
                      href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-whatsapp hover:text-[#128C7E] underline"
                    >
                      WhatsApp 627 21 33 47
                    </a>
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </section>

        {/* CONTACTO DIRECTO */}
        <section className="py-16 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-extrabold text-2xl md:text-[32px] text-text-primary text-center mb-10">
              También puedes contactarnos directamente
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <a
                href="tel:+34951120770"
                className="flex flex-col items-center gap-3 p-6 border border-border rounded-2xl hover:border-primary transition-colors"
              >
                <span className="text-3xl">📞</span>
                <span className="font-heading font-bold text-lg text-text-primary">951 12 07 70</span>
                <span className="font-body text-sm text-text-secondary text-center">L-V 9-19h, S 9-14h</span>
              </a>
              <a
                href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-3 p-6 border border-border rounded-2xl hover:border-whatsapp transition-colors"
              >
                <span className="text-3xl">💬</span>
                <span className="font-heading font-bold text-lg text-text-primary">627 21 33 47</span>
                <span className="font-body text-sm text-text-secondary text-center">WhatsApp 24h</span>
              </a>
              <a
                href="mailto:info@persianas-toldos-malaga.es"
                className="flex flex-col items-center gap-3 p-6 border border-border rounded-2xl hover:border-secondary transition-colors"
              >
                <span className="text-3xl">✉️</span>
                <span className="font-heading font-bold text-base text-text-primary text-center break-all">info@persianas-toldos-malaga.es</span>
                <span className="font-body text-sm text-text-secondary text-center">Email</span>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
              <span className="inline-flex items-center gap-2 font-body text-sm text-text-secondary">
                <span className="text-primary font-bold">✓</span> Sin compromiso
              </span>
              <span className="inline-flex items-center gap-2 font-body text-sm text-text-secondary">
                <span className="text-primary font-bold">✓</span> Visita gratis
              </span>
              <span className="inline-flex items-center gap-2 font-body text-sm text-text-secondary">
                <span className="text-primary font-bold">✓</span> Respuesta 24h
              </span>
            </div>
          </div>
        </section>
      </main>
      <CrossLinksFooter />
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}